import { z } from "zod";

/**
 * Contracts EARMARKED FUND — US-E20.F3-0209-S02 (@UC9331–@UC9333).
 *
 * 🔴 Tiền = `string` BigInt minor (KHÔNG number/float lộ ra JSON — B1). Mọi schema `.strict()`.
 *
 * 🔴🔴 KHOÁ CỨNG, KHÔNG SO-KHỚP MỜ: `purposeTag` là ENUM ĐỘNG do quỹ khai (tập mục đích đã khoá).
 * Expense chi từ quỹ có ràng buộc PHẢI khai `purposeTag` ∈ tập đó — sai/thiếu ⇒ 422 tại POST-TIME
 * (service, không chỉ form). Xem `packages/domain/src/earmark.ts` (nguồn sự thật thuần).
 */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;
/** khả dụng CÓ DẤU: âm = quỹ đang bội chi so với phần ràng buộc (hiển thị sự thật, không che). */
const SIGNED_MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/** Nhãn mục đích: slug ngắn, chuẩn hoá (trim+lower) ở biên. KHÔNG free-text dài dòng. */
export const PURPOSE_TAG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,49}$/;

export const purposeTagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(PURPOSE_TAG_PATTERN, "purposeTag chỉ gồm a-z 0-9 - _ (≤50 ký tự).");

export const EARMARK_DISPOSITION_VALUES = ["refund_donors", "reassign"] as const;
export const earmarkDispositionSchema = z.enum(EARMARK_DISPOSITION_VALUES);

export const EARMARK_RULE_STATUS_VALUES = ["active", "closed"] as const;
export const earmarkRuleStatusSchema = z.enum(EARMARK_RULE_STATUS_VALUES);

/* ─────────────────────────────── CONFIG (S02 AC1/AC2) ─────────────────────────────── */

/**
 * Cấu hình earmark của 1 quỹ: tập mục đích được phép + cờ khoá.
 * AC1: `purposeTags` RỖNG ⇒ reject (khoá mà không có mục đích nào = vô nghĩa/bẫy).
 */
export const earmarkConfigSchema = z
  .object({
    purposeTags: z.array(purposeTagSchema).min(1, "purposeTags phải có ít nhất 1 mục đích."),
    locked: z.boolean(),
  })
  .strict();
export type EarmarkConfig = z.infer<typeof earmarkConfigSchema>;

/* ─────────────────────────────── INPUT ─────────────────────────────── */

/**
 * Khoá tiền cho 1 mục đích (@UC9331): tạo ràng buộc + giữ chỗ `reservedMinor` đồng tiền THẬT đang
 * có trong quỹ. Ràng buộc vượt CASH khả dụng ⇒ 422 (hứa suông).
 */
export const setEarmarkSchema = z
  .object({
    purposeTag: purposeTagSchema,
    reservedMinor: z
      .string()
      .regex(MONEY_INTEGER_STRING_PATTERN)
      .refine(
        (v) => MONEY_INTEGER_STRING_PATTERN.test(v) && BigInt(v) > 0n,
        "reservedMinor phải > 0",
      ),
    /** Mặc định TRUE — earmark sinh ra là để KHOÁ. false = mở khoá (không enforce mục đích này). */
    locked: z.boolean().optional(),
    note: z.string().max(500).nullish(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type SetEarmarkRequest = z.infer<typeof setEarmarkSchema>;

/**
 * 🔴 @UC9333 — ĐÓNG mục đích + xử lý TIỀN DƯ MINH BẠCH. KHÔNG có lựa chọn "nhập quỹ chung im lặng":
 *  · `refund_donors` — hoàn lại người góp (dấu vết + lý do).
 *  · `reassign`      — chuyển sang mục đích ràng buộc KHÁC (`targetPurposeTag` BẮT BUỘC, phải là
 *    ràng buộc đang hiệu lực) — tiền VẪN bị khoá, chỉ đổi mục đích, và phải có `consentRef`
 *    (dấu vết ĐỒNG THUẬN: id phiên biểu quyết / biên bản).
 */
export const closeEarmarkSchema = z
  .object({
    disposition: earmarkDispositionSchema,
    targetPurposeTag: purposeTagSchema.optional(),
    /** Bằng chứng đồng thuận (consensusId / biên bản). BẮT BUỘC với `reassign`. */
    consentRef: z.string().min(1).max(200).optional(),
    reason: z.string().min(1).max(500),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict()
  .refine(
    (v) => v.disposition !== "reassign" || (!!v.targetPurposeTag && !!v.consentRef),
    "Chuyển mục đích khác BẮT BUỘC targetPurposeTag + consentRef (đồng thuận).",
  );
export type CloseEarmarkRequest = z.infer<typeof closeEarmarkSchema>;

/* ─────────────────────────────── RESPONSE ─────────────────────────────── */

export const earmarkRuleResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    purposeTag: z.string(),
    locked: z.boolean(),
    status: earmarkRuleStatusSchema,
    reservedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    disbursedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Phần ràng buộc CÒN LẠI (reserve − disburse − release) — chưa tiêu, không ai mượn được. */
    remainingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    note: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type EarmarkRuleResponse = z.infer<typeof earmarkRuleResponseSchema>;

/** 🔴 View "quỹ này còn tiêu được bao nhiêu": khả dụng = CASH − Σ ràng buộc chưa giải ngân. */
export const fundEarmarkSummaryResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    cashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    reservedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    availableMinor: z.string().regex(SIGNED_MONEY_INTEGER_STRING_PATTERN),
    rules: z.array(earmarkRuleResponseSchema),
  })
  .strict();
export type FundEarmarkSummaryResponse = z.infer<typeof fundEarmarkSummaryResponseSchema>;

export const closeEarmarkResponseSchema = z
  .object({
    ruleId: z.string().uuid(),
    purposeTag: z.string(),
    status: z.literal("closed"),
    disposition: earmarkDispositionSchema,
    /** Số tiền dư đã được xử lý (0 nếu đã tiêu hết đúng mục đích). */
    surplusMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    targetPurposeTag: z.string().nullable(),
    closedAt: z.string().datetime(),
  })
  .strict();
export type CloseEarmarkResponse = z.infer<typeof closeEarmarkResponseSchema>;

/* ─────────────────────────────── ERROR CODES (fail-loud, có tên) ─────────────────────────────── */

export const EARMARK_PURPOSE_MISMATCH_MESSAGE =
  "Chi sai mục đích của quỹ đã khoá mục đích.";
export const EARMARK_AVAILABLE_EXCEEDED_MESSAGE =
  "Không đủ số dư khả dụng — tiền đang ràng buộc mục đích KHÔNG được mượn tạm.";
